import { useEffect, useMemo } from "react";

import { MessageType } from "shared/types";
import { type Conversation, type Message } from "shared/apiClient";
import { EventTypes } from "@/types/events";

import { useCaptureEvent, CaptureEventType } from "@/hooks";

import {
  FinaliseDetailsStep,
  MultiSelectStep,
  ReviewProposalStep,
  SiteOverviewStep,
  TextMessageStep,
  TreeDistanceStep,
  TreeModuleStep,
  TreeSizeStep,
  YesNoListStep,
} from "./widgets";
import { NearbyTreesModuleStep } from "@/components/wizard/widgets/nearbyTreesModuleStep";
import { RemovingPruningTreesModuleStep } from "@/components/wizard/widgets/removingPruningTreesModuleStep";

export interface WizardStepProps {
  messages: Conversation["messages"];
  message?: Message;
}

export function RenderWizardStep({ messages, message }: WizardStepProps) {
  const { captureEvent } = useCaptureEvent();

  const isLatestMessage = useMemo(() => {
    return messages?.[messages.length - 1]?.id === message?.id;
  }, [messages, message]);

  useEffect(() => {
    console.log("<RenderWizardStep> message", message);
  }, [message]);

  useEffect(() => {
    if (message?.id) {
      captureEvent(CaptureEventType.USER_QUESTION_RENDERED, {
        messageId: message.id,
      });
    }
  }, [message?.id]); // eslint-disable-line

  useEffect(() => {
    if (message?.id) {
      const customEvent = new CustomEvent(EventTypes.ScrollToTop);
      document.dispatchEvent(customEvent);
    }
  }, [message?.id]);

  switch (message?.message_type) {
    case MessageType.STANDARD:
    case MessageType.AMENDMENT:
    case MessageType.OVERWRITE:
      return (
        <TextMessageStep
          key={message.id}
          message={message}
          showLoading={!isLatestMessage}
        />
      );
    case MessageType.CHOICES_QUESTION:
      return (
        <MultiSelectStep
          key={message.id}
          message={message}
          showLoading={!isLatestMessage}
        />
      );
    case MessageType.FINALISE_DETAILS:
      return (
        <FinaliseDetailsStep
          key={message.id}
          message={message}
          showLoading={!isLatestMessage}
        />
      );
    case MessageType.YES_NO_LIST_QUESTION:
      return (
        <YesNoListStep
          key={message.id}
          message={message}
          showLoading={!isLatestMessage}
        />
      );
    case MessageType.REVIEW_PROPOSAL:
      return (
        <ReviewProposalStep
          key={message.id}
          message={message}
          showLoading={!isLatestMessage}
        />
      );
    case MessageType.SITE_OVERVIEW:
      return (
        <SiteOverviewStep
          key={message.id}
          message={message}
          showLoading={!isLatestMessage}
        />
      );
    case MessageType.TREE_DIAMETER_QUESTION:
      return <TreeSizeStep message={message} showLoading={!isLatestMessage} />;
    case MessageType.TREE_DISTANCE_QUESTION:
      return (
        <TreeDistanceStep message={message} showLoading={!isLatestMessage} />
      );
    case MessageType.TREE_MODULE_QUESTION:
      return (
        <TreeModuleStep
          key={message.id}
          message={message}
          showLoading={!isLatestMessage}
        />
      );
    case MessageType.NEARBY_TREES_QUESTION:
      return (
        <NearbyTreesModuleStep
          key={message.id}
          message={message}
          showLoading={!isLatestMessage}
        />
      );
    case MessageType.REMOVING_OR_PRUNING_QUESTION:
      return (
        <RemovingPruningTreesModuleStep
          key={message.id}
          message={message}
          showLoading={!isLatestMessage}
        />
      );
    default:
      return null;
  }
}
